/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useState,
  PropsWithChildren,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  useEffect,
} from 'react'

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  inline,
  useInteractions,
  useClick,
  useFocus,
  useRole,
  useDismiss,
} from '@floating-ui/react'

import {
  ChevronDown,
  ChevronUp,
  XMark,
  MagnifyingGlass,
} from '@erudilabs/icons'
import { mergeClassName } from '@erudilabs/merge-class-utils'

import { TextInput } from '@erudilabs/text-input'

export type DropdownOptionsProps = {
  label: string
  value: string | number
  prefix?: any
}[]

export interface DropdownProps extends PropsWithChildren {
  testId?: string
  id?: string
  className?: string
  label?: string
  name: string
  placeholder?: string
  options: DropdownOptionsProps
  defaultValue?: string
  errorMessage?: string
  helper?: string
  disabled?: boolean
  onChange?: (arg0?: any) => void
  onBlur?: (e: FocusEvent) => void
  searchPlaceholder?: string
  searcNotFoundText?: string
  searchable?: boolean
  clearable?: boolean
  autoFocus?: boolean
  value?: string | number
}
export const Dropdown = forwardRef(
  (props: DropdownProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
      label,
      name,
      options,
      helper,
      className,
      clearable = true,
      searchable = true,
      searcNotFoundText,
      defaultValue,
      testId,
      id,
      autoFocus,
      errorMessage,
      searchPlaceholder,
      disabled,
      placeholder,
      onChange,
      onBlur,
      value,
    } = props
    const ARROW_HEIGHT = 8
    const GAP = 2

    const defaultSelected = options.filter(
      (x) =>
        String(x.value).toLowerCase() === String(defaultValue)?.toLowerCase() ||
        String(x.value).toLowerCase() === String(value).toLowerCase()
    )[0]

    const [isOpen, setIsOpen] = useState(false)
    const [currentLabel, setCurrentLabel] = useState(
      defaultSelected?.label || ''
    )
    const [currentValue, setCurrentValue] = useState(
      defaultSelected?.value || ''
    )
    const [searchValue, setSearchValue] = useState('')

    useEffect(() => {
      if (value !== currentValue) {
        const val = options.filter(
          (x) =>
            String(x.value).toLowerCase() === String(value).toLocaleLowerCase()
        )[0]
        if (val) {
          setCurrentLabel(val.label)
          setCurrentValue(val.value)
        } else if (value === 0) {
          setCurrentLabel('')
          setSearchValue('')
          setCurrentValue(value)
          onChange?.(value)
        }
      }
    }, [currentValue, value])

    const { x, y, strategy, refs, context } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      middleware: [
        inline(),
        offset(ARROW_HEIGHT + GAP),
        flip({ fallbackAxisSideDirection: 'start', crossAxis: false }),
      ],
      placement: 'bottom-start',
      whileElementsMounted: autoUpdate,
    })
    const click = useClick(context)
    const dismiss = useDismiss(context)
    const role = useRole(context)
    const focus = useFocus(context)

    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
      role,
      focus,
    ])
    const iconChevron = isOpen ? (
      <ChevronUp
        className={mergeClassName(
          disabled ? 'text-gray-500 dark:text-gray-700' : 'text-gray-600'
        )}
      />
    ) : (
      <ChevronDown
        className={mergeClassName(
          disabled ? 'text-gray-500 dark:text-gray-700' : 'text-gray-600'
        )}
      />
    )

    const optionsResult = options?.filter((x) => {
      if (searchValue?.length > 0) {
        return x.label.toLowerCase().includes(searchValue.toLowerCase())
      } else {
        return options
      }
    })

    return (
      <div className={mergeClassName('relative', className)}>
        {label && (
          <label
            htmlFor={name}
            className={mergeClassName(
              errorMessage
                ? 'text-red-600 dark:text-red-400'
                : 'text-black dark:text-white',
              'mb-2 block cursor-pointer text-left font-semibold'
            )}
          >
            {label}
          </label>
        )}
        <div
          data-testid={testId}
          id={id}
          ref={refs.setReference}
          {...getReferenceProps()}
          className={mergeClassName(
            disabled
              ? 'cursor-not-allowed pointer-events-none'
              : 'cursor-pointer',
            'relative'
          )}
        >
          <input
            type="hidden"
            name={name}
            value={currentValue}
            onChange={onChange}
            onBlur={onBlur}
          />
          <TextInput
            ref={ref}
            prefix={
              options.find((option) => option.value == currentValue)?.prefix
            }
            value={currentLabel}
            name={`${name}`}
            autoFocus={autoFocus}
            readOnly
            suffix={
              clearable && Boolean(currentValue) && !disabled ? (
                <XMark
                  className={mergeClassName(
                    'text-gray-600',
                    disabled
                      ? 'text-gray-500 dark:text-gray-700'
                      : 'text-gray-600'
                  )}
                  onClick={() => {
                    setCurrentLabel('')
                    setCurrentValue('')
                    setSearchValue('')
                    onChange?.('')
                  }}
                />
              ) : (
                iconChevron
              )
            }
            placeholder={placeholder}
            disabled={disabled}
            hideErrorMessage
            clearable
            errorMessage={errorMessage}
            onBlur={onBlur}
            onChange={onChange}
          />
        </div>
        {isOpen && (
          <div
            ref={refs.setFloating}
            className={mergeClassName([
              'text-left absolute z-20 py-2 text-sm transition-opacity duration-300 rounded-lg shadow-sm max-h-80 overflow-y-auto border',
              'bg-white text-gray-900 before:bg-white border-gray-200',
              'dark:bg-gray-900 dark:text-gray-200 before:dark:bg-black dark:border-gray-800',
            ])}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: '100%',
            }}
            {...getFloatingProps()}
          >
            <div className="relative">
              {searchable && (
                <div className="py-2 px-4">
                  <TextInput
                    prefix={<MagnifyingGlass className="text-gray-600" />}
                    name={`${name}-search-dropdown`}
                    placeholder={searchPlaceholder || 'Type a keyword ...'}
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value)
                    }}
                  />
                </div>
              )}
              {optionsResult.length > 0 ? (
                optionsResult.map((option, i) => {
                  return (
                    <ul key={i}>
                      <li
                        className={mergeClassName(
                          'py-2 px-4 block hover:bg-blue-50 dark:hover:bg-gray-800/20 cursor-pointer',
                          option.value === currentValue &&
                            'bg-blue-50 dark:bg-gray-800/20'
                        )}
                        onClick={() => {
                          setCurrentLabel(option.label)
                          setCurrentValue(option.value)
                          onChange?.(option.value)
                          setIsOpen(false)
                        }}
                      >
                        {option.prefix ? (
                          <div className="flex gap-2.5 items-center">
                            <div className="max-w-24 max-h-24">
                              {option.prefix}
                            </div>
                            <span>{option.label}</span>
                          </div>
                        ) : (
                          <span>{option.label}</span>
                        )}
                      </li>
                    </ul>
                  )
                })
              ) : (
                <div className="flex flex-col space-y-2 justify-center items-center p-5">
                  <span className="text-gray-700 dark:text-gray-400">
                    {searcNotFoundText || 'Not Found, try another keyword!'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        {helper && (
          <p className="mt-2 text-left text-xs font-semibold text-gray-600 first-letter:capitalize">
            {helper}
          </p>
        )}
        {errorMessage && (
          <p className="mt-2 text-left text-xs font-semibold text-red-600 dark:text-red-400 first-letter:capitalize">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)
